// Configuration localStorage 유틸리티
import { DeviceType } from '@packages/types/device/common/deviceTypes';

export interface SavedConfiguration {
  id: string;
  timestamp: string;
  name: string;
  configData: {
    deviceInfo: any;
    deviceConfig: Record<string, unknown>;
    deviceType: DeviceType;
    version: string;
  };
}

const STORAGE_KEY_LITE = 'bellcloud_configurations_lite';
const STORAGE_KEY_MINI = 'bellcloud_configurations_mini';
const MAX_STORED_CONFIGS = 10;

// 디바이스 타입에 따른 스토리지 키 반환
const getStorageKey = (deviceType: DeviceType): string => {
  return deviceType === DeviceType.MINI ? STORAGE_KEY_MINI : STORAGE_KEY_LITE;
};

/**
 * 구 포맷({ isLite, isMini, isFourGas }) → DeviceType string 마이그레이션
 */
const migrateDeviceType = (raw: any): DeviceType => {
  if (typeof raw === 'string' && Object.values(DeviceType).includes(raw as DeviceType)) {
    return raw as DeviceType;
  }
  // 구 포맷 객체 처리
  if (typeof raw === 'object' && raw !== null) {
    if (raw.isMini === true) return DeviceType.MINI;
    if (raw.isLite === true) return DeviceType.LITE;
  }
  return DeviceType.LITE;
};

/**
 * 저장된 설정 항목에서 구 포맷 deviceType을 마이그레이션
 */
const migrateConfig = (config: any): SavedConfiguration => {
  return {
    ...config,
    configData: {
      ...config.configData,
      deviceType: migrateDeviceType(config.configData?.deviceType),
    },
  };
};

// 설정 목록 가져오기
export const getSavedConfigurations = (deviceType: DeviceType): SavedConfiguration[] => {
  try {
    const key = getStorageKey(deviceType);
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    const configs = JSON.parse(stored) as any[];
    return configs
      .map(migrateConfig)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  } catch (error) {
    console.error('❌ 설정 목록 불러오기 실패:', error);
    return [];
  }
};

// 동일한 이름의 설정이 있는지 확인
export const hasConfigurationWithName = (name: string, deviceType: DeviceType): boolean => {
  try {
    const configs = getSavedConfigurations(deviceType);
    return configs.some(c => c.name === name.trim());
  } catch (error) {
    console.error('❌ 설정 이름 확인 실패:', error);
    return false;
  }
};

// 설정 저장 (키값은 필수)
export const saveConfiguration = (
  configData: SavedConfiguration['configData'],
  name: string,
  deviceType: DeviceType = DeviceType.LITE
): string => {
  try {
    if (!name || !name.trim()) {
      throw new Error('Configuration name is required');
    }

    const key = getStorageKey(deviceType);
    const existing = getSavedConfigurations(deviceType);

    // 동일한 이름이 이미 있으면 제거 (덮어쓰기)
    const filtered = existing.filter(c => c.name !== name.trim());

    const newConfig: SavedConfiguration = {
      id: `config_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      name: name.trim(),
      configData,
    };

    const updated = [newConfig, ...filtered];
    const limited = updated.slice(0, MAX_STORED_CONFIGS);

    localStorage.setItem(key, JSON.stringify(limited));
    return newConfig.id;
  } catch (error) {
    console.error('❌ 설정 저장 실패:', error);
    throw error;
  }
};

// 설정 삭제
export const deleteConfiguration = (id: string, deviceType: DeviceType): boolean => {
  try {
    const key = getStorageKey(deviceType);
    const existing = getSavedConfigurations(deviceType);
    const filtered = existing.filter(c => c.id !== id);
    localStorage.setItem(key, JSON.stringify(filtered));
    return true;
  } catch (error) {
    console.error('❌ 설정 삭제 실패:', error);
    return false;
  }
};
